// First Created — 21 Nov 2025
// Author — Atul Pandey
// Purpose — Hero section for ThinkWare: Thought Papers + Video Podcasts

import React from "react";
import { motion } from "framer-motion";
import { LightBulbIcon } from "@heroicons/react/24/outline";

const EtThinkWareHero = () => {
  return (
    <section
      id="thinkware-hero"
      className="relative py-24 pb-40 bg-gray-900 text-white overflow-hidden font-poppins"
    >
      {/* ---------------------------------------------- */}
      {/* BACKGROUND LAYERS */}
      {/* ---------------------------------------------- */}

      {/* Gradient Behind Everything */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black via-orange-800/40 to-black animate-[pulse_12s_ease-in-out_infinite]" />

      {/* Mobile-only Glow */}
      <div className="absolute inset-x-0 top-24 flex justify-center md:hidden pointer-events-none z-[5]">
        <div
          className="
            w-64 h-64 rounded-full 
            bg-[radial-gradient(circle_at_center,rgba(255,115,0,0.4)_0%,transparent_70%)] 
            blur-3xl opacity-70
          "
        />
      </div>

      {/* Bottom Fade for Smooth Merge into Library */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-black z-[4]" />

      {/* ---------------------------------------------- */}
      {/* MAIN GRID CONTENT */}
      {/* ---------------------------------------------- */}
      <div
        className="
          relative z-10
          max-w-7xl mx-auto px-4 sm:px-6 lg:px-8
          grid md:grid-cols-2 gap-12 items-center
        "
      >

        {/* ---------------------------------------------- */}
        {/* LEFT TEXT COLUMN */}
        {/* ---------------------------------------------- */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl"
        >
        
          <h2 className="text-3xl md:text-4xl font-bold text-orange-600 mb-6">
            ThinkWare
          </h2>

          <p className="text-gray-300 mb-6 text-lg leading-relaxed">
            A curated set of Eternity’s{" "}
            <span className="text-orange-600 font-semibold">Thought Provocation Sparks</span>{" "}
          . Video podcasts, thought pieces, and narrative insights designed to help
            tech-led leaders{" "}
            <span className="text-white font-semibold">
              induce a <span className="text-orange-600 font-semibold">Thinking</span>{" "}
              <span className="text-orange-600 font-semibold animate-pulse">Shift</span>
            </span>.
          </p>

          <p className="text-gray-300 mb-6 text-lg leading-relaxed">
            Each spark explores the deeper forces shaping{" "}
            <span className="text-orange-400">Purpose-Native Organizations</span>
          . Leadership habits, mind frames, execution patterns, and growth behaviors
            that power up high-growth performance.
          </p>

          <ul className="space-y-4 text-gray-300 font-extralight">
            <li className="flex items-start gap-3">
              <LightBulbIcon className="h-7 w-7 text-orange-500 shrink-0" />
              <span>
                Reflective content to help you challenge assumptions and reframe
                your leadership lens.
              </span>
            </li>

            <li className="flex items-start gap-3">
              <LightBulbIcon className="h-7 w-7 text-orange-500 shrink-0" />
              <span>
                Engagement prompts and challenges to activate deeper thinking and
                start your transformation journey.
              </span>
            </li>
          </ul>

          <p className="text-gray-300 mt-6 text-lg">
            This is where your inner shift begins.
          </p>
        </motion.div>

        {/* ---------------------------------------------- */}
        {/* RIGHT FLOATING ORB — DESKTOP ONLY */}
        {/* ---------------------------------------------- */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="hidden md:flex justify-center md:justify-end"
        >
          <div className="relative w-56 h-56 lg:w-72 lg:h-72">
            {/* Outer glow */}
            <div
              className="
                absolute inset-0 rounded-full 
                bg-[radial-gradient(circle_at_center,rgba(255,115,0,0.35)_0%,transparent_70%)]
                animate-pulse
              "
            />

            {/* Inner orb */}
            <div
              className="
                absolute inset-10 rounded-full
                bg-[radial-gradient(circle_at_center,rgba(255,140,0,0.5),transparent)]
                animate-[ping_4s_ease-in-out_infinite]
              "
            />

            {/* Ring outline */}
            <div
              className="
                absolute inset-0 rounded-full 
                border border-orange-500/20
              "
            />
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default EtThinkWareHero